import type { FreeTextOverlay, TelopFont } from '@shared/types'
import { useEditorStore } from '../../../../store/editorStore'
import { CheckboxField, SelectField, SliderField, TextField } from '../../../common/Field'
import { InspectorFrame } from './InspectorFrame'
import { OVERLAY_KIND_LABEL } from '../overlayMeta'
import { FONT_SELECT_LABEL, FONT_SELECT_ORDER } from './fontOptions'

/** task spec §3-3: 「FreeText は text / font / sizePx / color / strokeColor / x / y / vertical」。 */
export function FreeTextInspector({ overlay }: { overlay: FreeTextOverlay }) {
  const updateOverlay = useEditorStore((s) => s.updateOverlay)
  function patch(p: Partial<FreeTextOverlay>) {
    updateOverlay(overlay.id, p)
  }

  return (
    <InspectorFrame
      title={OVERLAY_KIND_LABEL.free}
      overlayId={overlay.id}
      overlayKind="free"
      startSec={overlay.startSec}
      durationSec={overlay.durationSec}
      minDurationSec={0.3}
      maxDurationSec={10}
    >
      <div data-testid="freetext-inspector" className="space-y-4">
        <TextField label="文言" value={overlay.text} onChange={(e) => patch({ text: e.target.value })} />
        <SelectField
          label="フォント"
          value={overlay.font}
          onChange={(e) => patch({ font: e.target.value as TelopFont })}
          data-testid="overlay-font-select"
        >
          {FONT_SELECT_ORDER.map((f) => (
            <option key={f} value={f}>
              {FONT_SELECT_LABEL[f]}
            </option>
          ))}
        </SelectField>
        <SliderField
          label="文字サイズ"
          value={overlay.sizePx}
          min={24}
          max={120}
          step={1}
          unit="px"
          onChange={(sizePx) => patch({ sizePx })}
        />
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[11px] font-medium text-text-muted mb-1">文字色</label>
            <input
              type="color"
              value={overlay.color}
              onChange={(e) => patch({ color: e.target.value })}
              className="w-full h-[30px] rounded bg-ink-bg/60 border border-ink-border cursor-pointer"
            />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-text-muted mb-1">縁取り色</label>
            <input
              type="color"
              value={overlay.strokeColor ?? '#000000'}
              onChange={(e) => patch({ strokeColor: e.target.value })}
              className="w-full h-[30px] rounded bg-ink-bg/60 border border-ink-border cursor-pointer"
            />
          </div>
        </div>
        <SliderField
          label="横位置 (x)"
          value={overlay.x}
          min={0}
          max={1}
          step={0.01}
          onChange={(x) => patch({ x })}
          data-testid="freetext-x"
        />
        <SliderField
          label="縦位置 (y)"
          value={overlay.y}
          min={0}
          max={1}
          step={0.01}
          onChange={(y) => patch({ y })}
          data-testid="freetext-y"
        />
        <CheckboxField label="縦書き" checked={overlay.vertical} onChange={(vertical) => patch({ vertical })} />
      </div>
    </InspectorFrame>
  )
}
